<template>
  <div>
    <Dropdown
      v-if="showAttachmentTypeCombo"
      :title="$lang.attachment_type"
      :label="$lang.attachment_type"
      :placeholder="$lang.attachment_type"
      :value="form.attachmentType"
      name="attachmentType"
      id="attachmentType"
      :vuelidateObj="$v.form.attachmentType"
      :options="attachmentTypeOptions"
      @input="input"
      @change="changeAttachmentType"
      @focus="resetValidation"
      @blur="checkValidation"
      @reset="changeAttachmentType"
    />
    <slide-y-up-transition>
      <div
        v-if="selectedAttachmentType"
        class="drag-uploader"
        :class="{ 'drag-uploader--invalid': errorMessage }"
      >
        <div class="drag-uploader__title">
          {{ label }}
          <span class="drag-uploader__formats">
            ({{ generateValidationText }})
          </span>
        </div>
        <!-- v-if="!files.length || multiple" -->
        <label for="file" class="drag-uploader__label" @drag="onChange">
          <mdicon name="cloud-upload-outline" size="24" class="d-flex" />
          <span class="drag-uploader__label-text">{{ $lang.attach_file }}</span>
          <input
            type="file"
            name="file"
            id="file"
            ref="file"
            :multiple="multiple"
            @change="onChange"
          />
        </label>
        <div v-if="errorMessage" class="drag-uploader__error">
          {{ errorMessage }}
        </div>
        <div v-if="files.length">
          <div
            class="drag-uploader__item"
            v-for="(file, index) in files"
            :key="index"
          >
            <div class="drag-uploader__right-box">
              <div class="drag-uploader__preview">
                <PieChart
                  v-if="file.progressWidth"
                  :percent="file.progressWidth"
                  :size="36"
                  :lineWidth="3"
                  barColor="#0085FF"
                  trackColor="#E0F0FF"
                />
                <b-img v-else :src="generateFilePreview(file.data)" />
              </div>
              <div class="drag-uploader__text-box">
                <div class="drag-uploader__type-box">
                  <span class="drag-uploader__caption">
                    {{ file.caption }}
                  </span>
                </div>
                <div class="drag-uploader__info-box">
                  <span class="drag-uploader__size">
                    {{ generateFileSize(file.data) }}
                  </span>
                  <span class="drag-uploader__name">{{
                    file.attachmentTypeCaption
                  }}</span>
                </div>
              </div>
            </div>
            <div class="drag-uploader__left-box">
              <MenuPopupWrapper
                v-if="hasEditTitle && !file.progressWidth"
                iconClass=""
                menuClass="left-0 top-5 z-index-1 padding-top-24"
                :items="itemButtons"
                :permissions="['admin', 'edit', 'view']"
                :showMenu="selectedMenuItem === file.clientCode"
                :rowItem="file.clientCode"
                @click="showItemMenu"
                @close="closeItemMenu"
              />
              <span
                v-if="!hasEditTitle && !file.progressWidth"
                @click.stop="remove(file.clientCode)"
              >
                <mdicon name="trash-can-outline" size="24" />
              </span>
            </div>
            <PopupWrapper
              :show="changeCaptionState === file.clientCode"
              wrapperClass="left-16 top-51"
              @close="closeChangeCaption"
            >
              <template>
                <b-form
                  @submit.prevent="closeChangeCaption"
                  v-click-outside="closeChangeCaption"
                >
                  <div
                    class="font-size-12 color-complementary-300 margin-bottom-8"
                  >
                    {{ $lang.add_title }}
                  </div>
                  <div class="position-relative input">
                    <b-form-input
                      v-model="file.caption"
                      autocomplete="off"
                      :autofocus="true"
                      trim
                      class="left-icon"
                      @input="changeCaption(file)"
                    />
                    <span
                      class="position-absolute left-16 top-10 color-complementary-200 cursor--pointer"
                      @click.stop="closeChangeCaption"
                    >
                      <mdicon name="check" size="20" />
                    </span>
                  </div>
                </b-form>
              </template>
            </PopupWrapper>
          </div>
        </div>
      </div>
    </slide-y-up-transition>
  </div>
</template>

<script>
import inputMixin from "@/mixin/inputMixin.vue";
import PieChart from "@/components/re-design/chart/PieChart.vue";
import MenuPopupWrapper from "@/components/re-design/modal/MenuPopupWrapper.vue";
import PopupWrapper from "@/components/re-design/modal/PopupWrapper.vue";
import { Dropdown } from "@/components/re-design/forms";
import { required } from "vuelidate/lib/validators";
import { pathOr, includes } from "ramda";

export default {
  mixins: [inputMixin],
  components: { PieChart, MenuPopupWrapper, PopupWrapper, Dropdown },
  props: {
    label: {
      type: String,
      default: "",
    },
    generateValidationText: {
      type: String,
      default: "",
    },
    errorMessage: {
      type: String,
      default: null,
    },
    progressWidth: {
      type: Number,
      default: 0,
    },
    files: {
      type: Array,
      default: () => [],
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    hasEditTitle: {
      type: Boolean,
      default: false,
    },
    attachmentTypeOptions: {
      type: Array,
      default: () => [],
    },
    selectedAttachmentType: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    maxCountUpload: {
      type: Number,
      default: 1,
    },
  },
  validations() {
    return {
      form: {
        attachmentType: { required },
        caption: {},
      },
    };
  },
  data() {
    return {
      selectedMenuItem: null,
      changeCaptionState: null,
      form: {
        attachmentType: null,
        caption: null,
      },
    };
  },
  computed: {
    itemButtons() {
      return [
        {
          caption: this.$lang.add_title,
          icon: "subtitles-outline",
          show: this.hasEditTitle,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.showChangeCaption,
        },
        {
          caption: this.$lang.delete,
          icon: "trash-can-outline",
          show: true,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.remove,
        },
      ];
    },
    showAttachmentTypeCombo() {
      const checkOptions = this.attachmentTypeOptions.length > 1;
      const checkMaxCountUpload = this.files.length < this.maxCountUpload;
      return checkOptions && checkMaxCountUpload;
    },
    checkMaxCountUpload() {
      return this.files.length < this.maxCountUpload;
    },
  },
  methods: {
    pathOr,
    includes,
    remove(fileCode) {
      this.$emit("remove", fileCode);
      this.$refs.file.value = null;
      this.closeItemMenu();
    },
    onChange(e) {
      const files = e.target.files || e.dataTransfer.files;

      this.$emit("onChange", files);
    },
    generateFilePreview(file) {
      if (!file) return;
      const fileType = file.type;
      const fileName = file.name.split(".")[1]; // get file by suffix name
      return fileType.match("image")
        ? URL.createObjectURL(file)
        : this.generateFileName(fileName);
    },
    generateFileName(key) {
      const names = {
        txt: "document",
        doc: "document",
        docx: "document",
        xls: "excel",
        xlsx: "excel",
        xlr: "excel",
        pdf: "pdf",
        mp3: "sound",
        "3gp": "sound",
        mp4: "video",
        mpeg: "video",
        rar: "zip",
        zip: "zip",
      };
      const name = pathOr(null, [key], names);
      return name
        ? `/img/shared/uploader-icons/${name}.svg`
        : `/img/shared/file-icon/${key}.svg`;
    },
    generateFileSize(file) {
      const bytes = pathOr(0, ["size"], file);
      const sizes = ["B", "KB", "MB", "GB"];
      if (bytes === 0) return "0B";
      const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
      return Math.round(bytes / Math.pow(1024, i), 2) + " " + sizes[i];
    },
    showItemMenu(clientCode) {
      this.selectedMenuItem =
        clientCode === this.selectedMenuItem ? null : clientCode;
    },
    closeItemMenu() {
      this.selectedMenuItem = null;
    },
    changeAttachmentType(item = null) {
      this.$emit("changeAttachmentType", item);
    },
    showChangeCaption(fileCode) {
      this.closeItemMenu();
      this.changeCaptionState =
        fileCode === this.changeCaptionState ? null : fileCode;
    },
    closeChangeCaption() {
      this.changeCaptionState = null;
    },
    changeCaption(file) {
      this.$emit("changeCaption", file);
    },
  },
};
</script>
